import {isFeatureEnabled} from '@github-ui/feature-flags'
import {fuzzyFilter} from '@github-ui/fuzzy-score/fuzzy-filter'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {BaseItemPickerProps} from '@github-ui/item-picker-base/base-item-picker-props'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {URLS} from '@github-ui/item-picker-base/constants/urls'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {PlusIcon} from '@primer/octicons-react'
import {SelectPanel, type SelectPanelItemProps as ItemProps} from '@primer/react'
import type React from 'react'
import {useCallback, useDeferredValue, useEffect, useMemo, useRef, useState} from 'react'

import styles from './LabelPickerBase.module.css'

export type LabelPickerLabel = {
  id: string
  databaseId?: number | null
  color: string
  name: string
  nameHTML: string
  description: string | null | undefined
  url: string
}

export type LabelPickerBaseProps = Omit<BaseItemPickerProps<LabelPickerLabel>, 'filterItems'> & {
  repo: string
  owner: string
  labels: LabelPickerLabel[]
  canCreateLabel: boolean
  suggestions?: LabelPickerLabel[]
  popularLabels?: LabelPickerLabel[]
  copilotSuggestedLabels?: LabelPickerLabel[]
  favoriteLabelIds?: string[]
  /**
   * When true (the server-gated "suggested_labels" experience is on), the
   * repo-popular and user-favorite labels are merged into a single "Suggestions"
   * group — favorites first, then popular — with a per-item reason subtext.
   * When false, popular and favorite render as their own separate groups.
   */
  enableSuggestedLabels?: boolean
  noLabelOption?: ExtendedItemProps<LabelPickerLabel>
  initialFilter?: string
  showEditLabelsButton?: boolean
  showNoMatchItem?: boolean
  showColors?: boolean
}

export type LabelPickerInternalProps = {
  totalNumberOfLabels: number
  searchLabels?: (query: string) => Promise<LabelPickerLabel[]>
  canCreateLabel: boolean
  repositoryId?: string
  setIsLoading?: (isLoading: boolean) => void
  onCreate?: CreateLabelMutationCallback
}

export type MutationCallback<TVariables, TResponse> = (
  variables: TVariables,
  options: {onSuccess: (response: TResponse | null) => void; onError: () => void},
) => void
export type CreateLabelVariables = {repositoryId: string; color: string; name: string}
export type CreateLabelMutationCallback = MutationCallback<CreateLabelVariables, LabelPickerLabel>

const DEFAULT_SUGGESTIONS: LabelPickerLabel[] = []
const DEFAULT_POPULAR_LABELS: LabelPickerLabel[] = []
const FILTERED_RESULTS_LIMIT = 30
// In the combined "Suggestions" experience, cap the total number of suggested
// labels (favorites + repo-popular) surfaced at the top of the picker so the
// group stays scannable. Favorites take precedence over popular labels.
const SUGGESTED_LABELS_LIMIT = 5

const REASON_FAVORITE = 'Recent'
const REASON_POPULAR = 'Popular'

// Analytics attribution for a suggestion selection: which source group the
// selected label was actually shown in. `search_result` covers everything that
// wasn't surfaced as a suggestion (typed-then-selected, or overflow beyond the
// suggestions cap).
const SUGGESTION_REASON = {
  favorite: 'favorite',
  popular: 'popular',
  copilot: 'copilot',
  searchResult: 'search_result',
} as const
type SuggestionReason = (typeof SUGGESTION_REASON)[keyof typeof SUGGESTION_REASON]

// Attributes a selected label to the source group it was actually shown in.
// In the combined "Suggestions" experience the group is capped at
// SUGGESTED_LABELS_LIMIT, so favorite/popular apply only to labels that were
// actually surfaced as suggestions (the capped sets). Overflow labels render
// under "Labels" and fall back to `searchResult`. When the experience is off,
// favorites/popular are uncapped, so the full arrays are used.
function resolveSuggestionReason(
  label: LabelPickerLabel,
  {
    enableSuggestedLabels,
    suggestedLabelIds,
    favoriteLabelIds,
    popularLabels,
    copilotSuggestedLabels,
  }: {
    enableSuggestedLabels: boolean
    suggestedLabelIds: {favorites: Set<string>; popular: Set<string>}
    favoriteLabelIds?: string[]
    popularLabels: LabelPickerLabel[]
    copilotSuggestedLabels?: LabelPickerLabel[]
  },
): SuggestionReason {
  const isFavorite = enableSuggestedLabels
    ? suggestedLabelIds.favorites.has(label.id)
    : (favoriteLabelIds ?? []).includes(label.id)
  if (isFavorite) return SUGGESTION_REASON.favorite

  const isPopular = enableSuggestedLabels
    ? suggestedLabelIds.popular.has(label.id)
    : popularLabels.some(l => l.id === label.id)
  if (isPopular) return SUGGESTION_REASON.popular

  if ((copilotSuggestedLabels ?? []).some(l => l.id === label.id)) return SUGGESTION_REASON.copilot

  return SUGGESTION_REASON.searchResult
}

const labelsGroup: ItemGroup = {groupId: 'labels', header: {title: 'Selected labels', variant: 'filled'}}
// When the suggested-labels experience is enabled, the selected group is titled
// simply "Selected" to sit cleanly above the "Suggestions"/"Labels" groups.
const selectedGroup: ItemGroup = {groupId: 'labels', header: {title: 'Selected', variant: 'filled'}}
const favoriteGroup: ItemGroup = {groupId: 'favorite', header: {title: 'Recent', variant: 'filled'}}
const suggestedGroup: ItemGroup = {groupId: 'suggested', header: {title: 'Suggestions', variant: 'filled'}}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}
// When the suggested-labels experience is enabled, the top "Suggestions" group
// holds favorites + popular, so the normal unselected list is relabeled "Labels"
// to disambiguate. Consumers with the feature off keep the original "Suggestions".
const labelsListGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Labels', variant: 'filled'}}
const copilotGroup: ItemGroup = {groupId: 'copilot', header: {title: 'Copilot suggestions', variant: 'filled'}}

export function LabelPickerBase({
  labels: selectedLabels,
  suggestions = DEFAULT_SUGGESTIONS,
  totalNumberOfLabels,
  copilotSuggestedLabels,
  popularLabels = DEFAULT_POPULAR_LABELS,
  favoriteLabelIds,
  enableSuggestedLabels = false,
  searchLabels,
  onSelectionChange,
  insidePortal,
  anchorElement,
  nested = false,
  triggerOpen = true,
  initialFilter,
  owner,
  repo,
  showEditLabelsButton = true,
  noLabelOption = undefined,
  title,
  showNoMatchItem = false,
  showColors = true,
  canCreateLabel,
  repositoryId,
  isLoading = false,
  setIsLoading,
  onCreate,
  anchorSide,
}: LabelPickerBaseProps & LabelPickerInternalProps) {
  const removeLabelsLoadingEnabled = isFeatureEnabled('issues_react_remove_labels_loading')
  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')
  const [filter, setFilter] = useState('')
  // Defer filter updates to keep input responsive during expensive list reconciliation
  const deferredFilterValue = useDeferredValue(filter)
  // The picker's item props (and their onAction handlers) are cached by the base
  // ItemPicker, so a handler can close over a stale `filter`. Mirror the latest
  // committed value into a ref and read it at selection time for accurate
  // "did the user search first?" telemetry.
  const filterRef = useRef(filter)
  useEffect(() => {
    filterRef.current = filter
  }, [filter])
  const filterTermCache = useRef<Set<string>>(new Set())
  const [isServerFetching, setIsServerFetching] = useState(false)
  const [loadedLabels, setLoadedLabels] = useState<Array<LabelPickerLabel & {__isNew__?: boolean}>>(suggestions)
  // Tracks the ids currently toggled on inside the open picker so the
  // suggestion-selection analytics event fires only for newly *added* labels
  // (not removals). Seeded from the already-applied labels.
  const liveSelectedIdsRef = useRef<Set<string>>(new Set(selectedLabels.map(l => l.id)))
  useEffect(() => {
    const ref = liveSelectedIdsRef.current
    return () => ref.clear()
  }, [])
  const {addToast} = useToastContext()
  const repoNwo = `${owner}/${repo}`

  // Synchronize loadedLabels with suggestions when they change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoadedLabels(suggestions)
  }, [suggestions])

  const shouldServerFetch = searchLabels && totalNumberOfLabels > suggestions.length

  const sortedSelectedLabels = useMemo(
    () => selectedLabels.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [selectedLabels],
  )

  // Memoize key functions to avoid recreating them on every render
  const getLabelName = useCallback((l: LabelPickerLabel) => l.name, [])
  const getLabelNameAndDescription = useCallback((l: LabelPickerLabel) => `${l.name} ${l.description ?? ''}`, [])

  // Resolve which favorite/popular labels actually belong to the combined
  // "Suggestions" group once, so the items list and group resolution stay in
  // sync. The group is capped at SUGGESTED_LABELS_LIMIT: favorites fill first
  // (in usage-ranked order), then popular labels take any remaining slots. Any
  // favorite/popular label beyond the cap is treated as a normal label.
  //
  // Selection is intentionally NOT considered when claiming these slots: the top
  // SUGGESTED_LABELS_LIMIT ranked labels are locked in regardless of what's
  // selected. Selecting a suggested label then just removes it from the group at
  // render time (it moves to "Selected") without backfilling a replacement, so
  // the visible suggestions shrink instead of pulling in the next-ranked label.
  const suggestedLabelIds = useMemo(() => {
    const favorites = new Set<string>()
    const popular = new Set<string>()
    if (!enableSuggestedLabels) {
      return {favorites, popular}
    }

    const relevantLabels = removeLabelsLoadingEnabled ? loadedLabels : suggestions

    for (const id of favoriteLabelIds ?? []) {
      if (favorites.size >= SUGGESTED_LABELS_LIMIT) break
      const label = relevantLabels.find(l => l.id === id)
      if (label) {
        favorites.add(label.id)
      }
    }

    for (const label of popularLabels) {
      if (favorites.size + popular.size >= SUGGESTED_LABELS_LIMIT) break
      if (!favorites.has(label.id)) {
        popular.add(label.id)
      }
    }

    return {favorites, popular}
  }, [enableSuggestedLabels, removeLabelsLoadingEnabled, loadedLabels, suggestions, favoriteLabelIds, popularLabels])

  // Membership is checked once per rendered label across several collections, so
  // resolve each collection to an id Set up front. This keeps the per-label
  // classification (in `items` and `resolveGroupId`) O(1) instead of scanning
  // every array with `.some()` on a hot path that can hold ~1000 labels.
  const lookupSets = useMemo(
    () => ({
      selectedIds: new Set(selectedLabels.map(l => l.id)),
      favoriteIds: new Set(favoriteLabelIds ?? []),
      popularIds: new Set(popularLabels.map(l => l.id)),
      copilotIds: new Set((copilotSuggestedLabels ?? []).map(l => l.id)),
    }),
    [selectedLabels, favoriteLabelIds, popularLabels, copilotSuggestedLabels],
  )

  const items = useMemo(() => {
    if (!deferredFilterValue) {
      const relevantLabels = removeLabelsLoadingEnabled ? loadedLabels : suggestions
      const {selectedIds} = lookupSets

      // First, collect all the labels that are already selected
      const allLabels = [...sortedSelectedLabels]

      // Resolve favorite ids to label objects in O(1) via an id->label map of the
      // loaded pool, rather than scanning it once per favorite id.
      const labelById = new Map(relevantLabels.map(l => [l.id, l] as const))

      // In the combined "Suggestions" experience, user favorites are surfaced
      // above repo-popular labels. Resolve the capped favorite ids to label
      // objects from the loaded pool, preserving their usage-ranked order.
      // Selected labels are dropped here (they render in the "Selected" group)
      // without freeing their slot, so the group shrinks rather than backfilling.
      const suggestedFavoriteLabels = (favoriteLabelIds ?? [])
        .filter(id => suggestedLabelIds.favorites.has(id))
        .map(id => labelById.get(id))
        .filter((l): l is LabelPickerLabel => l != null && !selectedIds.has(l.id))
      const suggestedFavoriteIds = new Set(suggestedFavoriteLabels.map(l => l.id))

      // Repo-popular labels are only surfaced (up to the cap) when the
      // suggested-labels feature is enabled; otherwise they fall into the normal
      // list like any other label.
      const suggestedPopularLabels = popularLabels.filter(
        l => suggestedLabelIds.popular.has(l.id) && !selectedIds.has(l.id),
      )
      const suggestedPopularIds = new Set(suggestedPopularLabels.map(l => l.id))

      // Then add unselected copilot labels that aren't in selected, favorite, or popular labels
      const unselectedCopilotLabels = (copilotSuggestedLabels ?? []).filter(
        l => !selectedIds.has(l.id) && !suggestedFavoriteIds.has(l.id) && !suggestedPopularIds.has(l.id),
      )
      const unselectedCopilotIds = new Set(unselectedCopilotLabels.map(l => l.id))

      // Then add unselected regular labels that aren't already surfaced above.
      // Favorite/popular labels beyond the cap fall through to here and render
      // as normal labels.
      const unselectedRegularLabels = relevantLabels.filter(
        l =>
          !selectedIds.has(l.id) &&
          !suggestedFavoriteIds.has(l.id) &&
          !suggestedPopularIds.has(l.id) &&
          !unselectedCopilotIds.has(l.id),
      )

      // Combine all labels (favorites first when combined, then popular)
      allLabels.push(
        ...suggestedFavoriteLabels,
        ...suggestedPopularLabels,
        ...unselectedCopilotLabels,
        ...unselectedRegularLabels,
      )

      // If we render the picker with all the labels we loaded it will cause some serious jank.
      // Instead, only render a sensible page size of labels.
      return allLabels.slice(0, VALUES.labelsInitialLoadCount)
    }

    // For filtered results, include both loaded labels and copilot labels without duplicates
    const uniqueItemsMap = new Map()

    // Add loaded labels first
    for (const label of loadedLabels) {
      uniqueItemsMap.set(label.id, label)
    }
    // Add copilot labels, but only if they're not already in the map
    for (const label of copilotSuggestedLabels ?? []) {
      if (!uniqueItemsMap.has(label.id)) {
        uniqueItemsMap.set(label.id, label)
      }
    }

    const itemsToFilter = Array.from(uniqueItemsMap.values())

    return fuzzyFilter<LabelPickerLabel>({
      items: itemsToFilter,
      filter: deferredFilterValue,
      key: getLabelName,
      secondaryKey: getLabelNameAndDescription,
    }).slice(0, FILTERED_RESULTS_LIMIT)
  }, [
    deferredFilterValue,
    loadedLabels,
    removeLabelsLoadingEnabled,
    suggestions,
    sortedSelectedLabels,
    copilotSuggestedLabels,
    popularLabels,
    favoriteLabelIds,
    suggestedLabelIds,
    lookupSets,
    getLabelName,
    getLabelNameAndDescription,
  ])

  const fetchSearchData = useCallback(
    async (query: string) => {
      if (!removeLabelsLoadingEnabled && query === '') {
        setLoadedLabels(suggestions)
        setIsServerFetching(false)
        return
      }

      if (removeLabelsLoadingEnabled && filterTermCache.current.has(query)) {
        setIsServerFetching(false)
        return
      }

      if (!searchLabels) return
      setIsServerFetching(true)

      try {
        setLoadedLabels(
          removeLabelsLoadingEnabled
            ? uniqById([...loadedLabels, ...(await searchLabels(query))])
            : await searchLabels(query),
        )
      } catch {
        // Temporarily disabling this until we have a standard way of displaying fetch errors
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchLabels,
        })
      } finally {
        setIsServerFetching(false)
        if (removeLabelsLoadingEnabled) {
          filterTermCache.current.add(query)
        }
      }
    },
    [removeLabelsLoadingEnabled, searchLabels, suggestions, loadedLabels, addToast],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter !== trimmedFilter && shouldServerFetch) {
        setIsServerFetching(true)
        debounceFetchSearchData(trimmedFilter)
        setFilter(value)
        return
      }
      // Making sure we set the filter in case the user types in
      // a value before we complete the initial load of suggestions
      if (suggestions.length !== 0) setFilter(value)
    },
    [debounceFetchSearchData, filter, shouldServerFetch, suggestions.length],
  )

  const getItemKey = useCallback((label: LabelPickerLabel) => label.id, [])

  const createNewLabel = useCallback(
    (
      _: ItemProps,
      label: LabelPickerLabel,
      event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (!repositoryId || !onCreate || !setIsLoading) return
      setIsLoading(true)

      onCreate(
        {
          repositoryId,
          color: label.color,
          name: label.name,
        },
        {
          onError: () => {
            // Temporarily disabling this until we have a standard way of displaying fetch errors
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotCreateLabel,
            })
            setIsLoading(false)
          },
          onSuccess: response => {
            if (response) {
              setLoadedLabels([{...response, __isNew__: true}, ...loadedLabels])
            }
            setIsLoading(false)
          },
        },
      )
    },
    [addToast, loadedLabels, onCreate, repositoryId, setIsLoading],
  )

  const resolveGroupId = useCallback(
    (label: LabelPickerLabel): string => {
      if (noLabelOption && label.id === SPECIAL_VALUES.noLabelsData.id) {
        return noLabelOption.selected ? labelsGroup.groupId : suggestionsGroup.groupId
      }

      const {selectedIds, favoriteIds, copilotIds} = lookupSets
      // Only favorites/popular labels that made it into the capped suggestions
      // group are grouped as suggestions; the rest render as normal labels.
      const isSuggestedLabel = suggestedLabelIds.favorites.has(label.id) || suggestedLabelIds.popular.has(label.id)

      // In the combined "Suggestions" experience, favorites take precedence over
      // popular so a label that is both is shown once, as a favorite.
      if (selectedIds.has(label.id)) {
        return labelsGroup.groupId
      } else if (enableSuggestedLabels && isSuggestedLabel) {
        return suggestedGroup.groupId
      } else if (!enableSuggestedLabels && favoriteIds.has(label.id)) {
        return favoriteGroup.groupId
      } else if (copilotIds.has(label.id)) {
        return copilotGroup.groupId
      }
      return suggestionsGroup.groupId
    },
    [noLabelOption, lookupSets, suggestedLabelIds, enableSuggestedLabels],
  )

  // Emit a hydro event whenever a label is *added* from the open picker so we can
  // answer: do users pick a shown suggestion (favorite/popular) directly, or do
  // they type a search term first and then select? `hadSearchTerm` is the primary
  // signal; `reason` attributes the source group. Deselections are tracked in the
  // ref but not reported. Fires on pointer/Enter activation via the item's
  // `onAction`; the base picker's space-key toggle path is not instrumented.
  const emitLabelSelectionAnalytics = useCallback(
    (label: LabelPickerLabel) => {
      if (liveSelectedIdsRef.current.has(label.id)) {
        liveSelectedIdsRef.current.delete(label.id)
        return
      }
      liveSelectedIdsRef.current.add(label.id)

      const trimmedFilter = filterRef.current.trim()
      const reason = resolveSuggestionReason(label, {
        enableSuggestedLabels,
        suggestedLabelIds,
        favoriteLabelIds,
        popularLabels,
        copilotSuggestedLabels,
      })

      sendEvent('item-picker-label-suggestion-select', {
        hadSearchTerm: trimmedFilter.length > 0,
        searchTermLength: trimmedFilter.length,
        reason,
        enableSuggestedLabels,
      })
    },
    [favoriteLabelIds, popularLabels, copilotSuggestedLabels, enableSuggestedLabels, suggestedLabelIds],
  )

  const convertToItemProps = useCallback(
    (label: LabelPickerLabel): ExtendedItemProps<LabelPickerLabel> => {
      if (noLabelOption && label.id === SPECIAL_VALUES.noLabelsData.id) {
        const noLabelItem = {...noLabelOption}
        noLabelItem.groupId = noLabelItem.selected ? labelsGroup.groupId : suggestionsGroup.groupId
        return noLabelItem
      }

      if (label.id === filter) {
        return {
          id: label.id,
          description: label.description || undefined,
          descriptionVariant: 'block',
          source: label,
          text: `Create new label: "${label.name}"`,
          trailingVisual: PlusIcon,
          className: styles.labelPickerItem,
          onAction: (item: ItemProps, event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) =>
            createNewLabel(item, label, event),
        }
      }

      const groupId = resolveGroupId(label)
      // Only combined-experience favorite/popular labels carry a reason subtext.
      const reasonText =
        groupId === suggestedGroup.groupId
          ? lookupSets.favoriteIds.has(label.id)
            ? REASON_FAVORITE
            : REASON_POPULAR
          : undefined

      return {
        id: label.id,
        description: label.description ?? undefined,
        descriptionVariant: 'block',
        children: (
          <VerifiedHTMLText
            // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
            html={label.nameHTML as SafeHTMLString}
            className={styles.labelNameText}
          />
        ),
        source: label,
        groupId,
        onAction: () => emitLabelSelectionAnalytics(label),
        trailingVisual: reasonText ? () => <span className={styles.reasonText}>{reasonText}</span> : undefined,
        leadingVisual: showColors
          ? () => (
              <div
                style={{
                  background: `#${label.color}`,
                  borderColor: `#${label.color}`,
                }}
                className={styles.labelColorBox}
              />
            )
          : undefined,
      }
    },
    [noLabelOption, filter, lookupSets, resolveGroupId, createNewLabel, showColors, emitLabelSelectionAnalytics],
  )

  const groups: ItemGroup[] = useMemo(() => {
    // Primer renders every supplied group heading, even when no rendered item
    // belongs to it. Item construction can drop labels that don't resolve from
    // the loaded pool (e.g. a stale/deleted favorite id) and a popular-only repo
    // has nothing left for the normal list, so deriving headings from the raw
    // id lists leaves empty sections. Instead, drive the headings off the group
    // ids the rendered items actually resolve to.
    const presentGroupIds = new Set(items.map(item => resolveGroupId(item)))
    const itemGroups: ItemGroup[] = []

    if (presentGroupIds.has(labelsGroup.groupId)) {
      itemGroups.push(enableSuggestedLabels ? selectedGroup : labelsGroup)
    }

    if (enableSuggestedLabels) {
      // Combined experience: a single "Suggestions" group holding both favorites
      // (first) and popular labels, directly above the normal list.
      if (presentGroupIds.has(suggestedGroup.groupId)) {
        itemGroups.push(suggestedGroup)
      }
    } else if (presentGroupIds.has(favoriteGroup.groupId)) {
      // Feature disabled: fall back to the original behavior — only user
      // favorites are surfaced as their own group. Repo-popular labels are not
      // shown separately.
      itemGroups.push(favoriteGroup)
    }

    if (presentGroupIds.has(copilotGroup.groupId)) {
      itemGroups.push(copilotGroup)
    }

    if (enableSuggestedLabels) {
      // Combined experience: the normal list is relabeled "Labels" and only
      // rendered when items actually resolve to it, so no empty heading shows.
      if (presentGroupIds.has(suggestionsGroup.groupId)) {
        itemGroups.push(labelsListGroup)
      }
    } else if (
      presentGroupIds.has(suggestionsGroup.groupId) ||
      presentGroupIds.has(favoriteGroup.groupId) ||
      presentGroupIds.has(copilotGroup.groupId)
    ) {
      // Feature disabled: the "Suggestions" list accompanies any Favorite or
      // Copilot group. The picker only draws group headings when more than one
      // group is present, so without this companion a lone Favorite/Copilot
      // group (every unselected label is a favorite or Copilot suggestion) would
      // render its items flat with no heading.
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [items, resolveGroupId, enableSuggestedLabels])

  const editLabelsPath = useMemo(() => {
    if (!repoNwo || !ssrSafeDocument?.location) return undefined
    const baseUrl = ssrSafeDocument.location.origin
    return `${baseUrl}/${repoNwo}${URLS.labelPageSuffix}`
  }, [repoNwo])

  const onEditLabels = useCallback(
    (e: React.MouseEvent) => {
      if (ssrSafeDocument?.location?.href && showEditLabelsButton && editLabelsPath) {
        // if the mouse wheel is clicked, do nothing
        if (e.button === 1) return

        // if a keyboard modifier is used, do nothing as the underlying default logic will
        // already open this into a new tab.
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

        e.preventDefault()
        ssrSafeDocument.location.href = editLabelsPath
      }
    },
    [editLabelsPath, showEditLabelsButton],
  )

  const anchorRef = useRef<HTMLButtonElement>(null)

  const showNoMatchMessage = useMemo(() => {
    const noPickerItems = !isServerFetching && !filter && items.length === 0
    const noFilterResults = !(showNoMatchItem && canCreateLabel && !isServerFetching) && !!filter && items.length === 0
    return noPickerItems || noFilterResults
  }, [isServerFetching, filter, items.length, showNoMatchItem, canCreateLabel])

  return (
    <div className={styles.labelPickerContainer}>
      <ItemPicker
        loading={removeLabelsLoadingEnabled ? isLoading : isLoading || isServerFetching}
        items={items}
        initialSelectedItems={selectedLabels}
        title={title || LABELS.labelsHeader}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={'Filter labels'}
        selectionVariant={items.length === 0 ? 'single' : 'multiple'}
        onSelectionChange={onSelectionChange}
        renderAnchor={props => anchorElement(props, anchorRef)}
        groups={!filter ? groups : undefined}
        insidePortal={insidePortal}
        height={enableSuggestedLabels ? 'xlarge' : 'large'}
        width={'medium'}
        nested={nested}
        resultListAriaLabel={'Label results'}
        triggerOpen={triggerOpen}
        initialFilter={initialFilter}
        selectPanelRef={anchorRef}
        keybindingCommandId="item-pickers:open-labels"
        eventsTag="label"
        anchorSide={anchorSide}
        customNoMatchItem={
          canCreateLabel && showNoMatchItem && filter && !isServerFetching
            ? {
                id: filter,
                color: 'aaaaaa',
                name: filter,
                nameHTML: filter,
                description: undefined,
                url: '',
              }
            : undefined
        }
        suppressNoMatchItem
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={
          showNoMatchMessage
            ? {
                title: 'No labels were found',
                body: 'Try searching with a different query for results.',
                variant: 'empty',
              }
            : undefined
        }
        secondaryAction={
          showEditLabelsButton ? (
            <SelectPanel.SecondaryActionLink onClick={onEditLabels} href={editLabelsPath} size={'small'}>
              {LABELS.editLabels}
            </SelectPanel.SecondaryActionLink>
          ) : undefined
        }
        responsiveOnNarrow={responsiveOnNarrowEnabled}
        focusPrependedElements
      />
    </div>
  )
}

const uniqById = (arr: LabelPickerLabel[]) => {
  return arr.filter((i, idx, a) => a.findIndex(item => item.id === i.id) === idx)
}
